import React, { lazy, Suspense, useMemo } from 'react';

import { Modal, ModalBody, Spinner } from '@/_components';
import NewAgreement from '@/_pages/agreements/new/modal';
import NewCompany from '@/_pages/companies/new/modal';
import NewCompetitor from '@/_pages/competitors/new/modal';
import NewContact from '@/_pages/contacts/new/modal';
import NewDomain from '@/_pages/domains/new/modal';
import NewInventoryVendor from '@/_pages/settings/inventory-vendors/new/modal';
import NewManufacturer from '@/_pages/settings/manufacturers/new/modal';
import NewTeamMember from '@/_pages/team-members/new/modal';
import { hotKeys, useBoolean, useShortcutKey } from '@/_shared';

import { QuickAddMenuContext } from './context';
import { type QuickActionPaths, quickActions, type QuickAddMenuContextType, type QuickAddQuerySchema } from './model';

import { useAuthContext } from '../auth-provider';

const LazyArPaymentModal = lazy(() =>
  import('@/_pages/account-receivables/new/components/ar-payment-modal').then((m) => ({
    default: m.ArPaymentModal,
  }))
);

const quickActionModals: Readonly<Partial<Record<QuickActionPaths, { Page: React.FC; title: string }>>> = {
  '/team-members/new': { Page: NewTeamMember, title: 'New Team Member' },
  '/contacts/new': { Page: NewContact, title: 'New Contact' },
  '/companies/new': { Page: NewCompany, title: 'New Company' },
  '/domains/new': { Page: NewDomain, title: 'New Domain' },
  '/competitors/new': { Page: NewCompetitor, title: 'New Competitor' },
  '/agreements/new': { Page: NewAgreement, title: 'New Agreement' },
  '/settings/manufacturers/new': {
    Page: NewManufacturer,
    title: 'New Manufacturer',
  },
  '/settings/inventory-vendors/new': {
    Page: NewInventoryVendor,
    title: 'New Inventory Vendor',
  },
} as const;

export function QuickAddProvider({ children }: { children: React.ReactNode }) {
  const [isArPaymentsOpen, openArPayment] = useBoolean();
  const [activeAction, setActiveAction] = React.useState<QuickActionPaths | null>(null);
  const [metadata, setMetadata] = React.useState<Partial<QuickAddQuerySchema> | null>(null);
  const triggerChangeCallback = React.useRef<(item: unknown) => void>(undefined);
  const { permissions } = useAuthContext();

  const hideModal: QuickAddMenuContextType['hideModal'] = React.useCallback((data) => {
    if (data && typeof triggerChangeCallback.current === 'function') {
      triggerChangeCallback.current(data);
    }
    setActiveAction(null);
    setMetadata(null);
  }, []);

  const showModal: QuickAddMenuContextType['showModal'] = React.useCallback(
    (action, _triggerChangeCallback, _metadata) => {
      console.log(action, _metadata);

      if (action === '/account-receivables/new') {
        openArPayment.setTrue();
        return;
      }

      setActiveAction(action);
      triggerChangeCallback.current = _triggerChangeCallback;
      setMetadata(_metadata || null);
    },
    [openArPayment]
  );

  const filteredQuickActions = useMemo(() => {
    const { model: modelPermissions } = permissions;
    if (!modelPermissions) {
      return [];
    }

    const allowedActions: typeof quickActions = [];

    quickActions.forEach((item) => {
      const permission = modelPermissions.find((mp) => mp.resource === item.model_name);
      if (permission?.create) {
        allowedActions.push(item);
      }
    });

    return allowedActions;
  }, [permissions]);

  const value: QuickAddMenuContextType = React.useMemo(
    () => ({
      activeAction,
      metadata,
      showModal,
      hideModal,
      filteredQuickActions,
    }),
    [activeAction, metadata, showModal, hideModal, filteredQuickActions]
  );

  const activeActionModal = activeAction && quickActionModals[activeAction];
  const PageModal = activeActionModal?.Page;
  const shouldOpen = !!activeActionModal;

  useShortcutKey(hotKeys.accountReceivable, () => {
    const p = filteredQuickActions.find((t) => t.model_name === 'account-receivables');
    if (p) {
      showModal(p.path);
    }
  });

  return (
    <QuickAddMenuContext value={value}>
      <Modal
        showBackdropCloseButton
        disableBackdropClosing
        className="bg-c-background overflow-hidden dark:bg-gray-700"
        size="lg"
        open={shouldOpen}
        onClose={() => hideModal()}
      >
        <ModalBody className="flex max-h-[calc(100vh-3rem)] flex-col overflow-y-auto p-0 pb-6">
          {PageModal ? <PageModal /> : null}
        </ModalBody>
      </Modal>

      {isArPaymentsOpen && (
        <Suspense
          fallback={
            <div className="fixed z-100 flex h-screen w-screen items-center justify-center bg-transparent">
              <Spinner />
            </div>
          }
        >
          <LazyArPaymentModal open={isArPaymentsOpen} onClose={openArPayment.setFalse} />
        </Suspense>
      )}

      {children}
    </QuickAddMenuContext>
  );
}
